import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { initPolicyRule } from './constants'
import { getPolicyRulePayload, mapRuleToState } from './helpers'

const usePolicyRule = ({ id, storeId, setToastInfo, storeCurrencyCode, timeZone }) => {
  const isDuplicate = id?.includes('duplicate-')
  const isNew = id === 'new'
  const ruleId = isDuplicate ? id.replace('duplicate-', '') : id

  const [hasChanges, setHasChanges] = useState(false)
  const [policyRule, setPolicyRule] = useState(initPolicyRule)
  const [errors, setErrors] = useState([])
  const originalRule = useRef(null)
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { fetchData: fetchPolicyRule, loading: fetchingPolicyRule } = useFetch(configurationServices.getPolicyRule, {
    doInitialRequest: false,
  })

  const { fetchData: createPolicyRule, loading: creatingPolicyRule } = useFetch(
    configurationServices.createPolicyRule,
    { doInitialRequest: false }
  )

  const { fetchData: editPolicyRuleRequest, loading: editingPolicyRule } = useFetch(
    configurationServices.editPolicyRule,
    { doInitialRequest: false }
  )

  const loadRule = useCallback(async () => {
    if (isNew) return

    try {
      const ruleData = await fetchPolicyRule({ storeId, ruleId })
      const mapped = mapRuleToState(ruleData, timeZone)
      originalRule.current = mapped
      setPolicyRule(mapped)
      if (isDuplicate) {
        setHasChanges(true)
      }
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }, [isNew, isDuplicate, fetchPolicyRule, storeId, ruleId, setToastInfo, timeZone])

  useEffect(() => {
    loadRule()
  }, [loadRule])

  const revertChanges = () => {
    if (isNew || isDuplicate) {
      navigate(routes.configurationPage.returnPolicy.policyRules)
      return
    }

    if (originalRule.current) {
      setPolicyRule(originalRule.current)
    }
    setErrors([])
    setHasChanges(false)
  }

  const onChangePolicyRule = paramsEdited => {
    setPolicyRule(prev => ({ ...prev, ...paramsEdited }))
    setHasChanges(true)
  }

  const onChangeActionConfig = partialConfig => {
    setPolicyRule(prev => ({ ...prev, action_config: { ...prev.action_config, ...partialConfig } }))
    setHasChanges(true)
  }

  const saveChanges = async currentPolicyRule => {
    const payload = getPolicyRulePayload(currentPolicyRule, storeCurrencyCode)

    if (isNew || isDuplicate) {
      try {
        await createPolicyRule({ storeId, policyRule: payload })
        setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
        navigate(routes.configurationPage.returnPolicy.policyRules)
      } catch (error) {
        setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      }
    } else {
      try {
        await editPolicyRuleRequest({ storeId, policyRule: payload })
        setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
        setHasChanges(false)
        navigate(routes.configurationPage.returnPolicy.policyRules)
      } catch (error) {
        setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      }
    }
  }

  return {
    policyRule,
    errors,
    setErrors,
    hasChanges,
    revertChanges,
    onChangePolicyRule,
    onChangeActionConfig,
    saveChanges,
    fetchingPolicyRule,
    creatingPolicyRule,
    editingPolicyRule,
    isNew,
    isDuplicate,
  }
}

export default usePolicyRule
